/* global $ */
import formatDateToYYYYMMDD from '../../../utils/formatDateToYYYYMMDD';
import { getEventEmoji } from '../../../constants/eventTypes';
import { createPickerButton, createEventBadge, createBadgesContainer, createEventEmoji } from './elementHelpers';
import { mapEventsByDate, mapEventsToBadges, filterEventsByVisibleMonth } from './eventDataHelpers';

// ============================================================================
// CONSULTAS DEL PICKER (Genéricas)
// ============================================================================

/**
 * Gets the current visible month from the picker
 * Returns the first day of the visible month, or today if it's the current month
 * @param {jQuery} $input - jQuery input element
 * @returns {Date|null} Date object representing the start date for the visible month, or null if picker not available
 */
export const getVisibleMonth = ($input) => {
  if (!$input) return null;

  const picker = $input.pickadate('picker');
  if (!picker) return null;

  // Get the highlighted date (which represents the visible month)
  const highlight = picker.get('highlight');
  if (!highlight || !highlight.obj) return null;

  const highlightDate = highlight.obj;
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Check if the visible month is the current month
  const isCurrentMonth = 
    highlightDate.getFullYear() === today.getFullYear() &&
    highlightDate.getMonth() === today.getMonth();

  if (isCurrentMonth) {
    // If it's the current month, return today's date
    return new Date(today);
  } 
    // Otherwise, return the first day of the visible month
    return new Date(highlightDate.getFullYear(), highlightDate.getMonth(), 1);
  
};

/**
 * Generic function to set picker view to a specific date
 * Navigates the calendar to show the specified date without selecting it
 * 
 * @param {Object} config - Configuration object
 * @param {jQuery} config.$input - jQuery input element
 * @param {Date} config.date - Date to navigate to
 * @param {Object} config.options - Options object
 * @param {boolean} config.options.openIfClosed - Whether to open calendar if closed (default: false)
 * @param {boolean} config.options.muted - Whether to mute events (default: true)
 */
export const setPickerView = ({ $input, date, options = {} }) => {
  if (!$input || !date) return;

  const picker = $input.pickadate('picker');
  if (!picker) return;

  const { openIfClosed = false, muted = true } = options;

  // Check if calendar is open
  const isOpen = picker.get('open');
  
  // If closed and openIfClosed is true, open it first
  if (!isOpen && openIfClosed) {
    picker.open();
  }
  
  // Set highlight to navigate to the specified date
  // This will move the calendar view without selecting a date
  const year = date.getFullYear();
  const month = date.getMonth();
  const day = date.getDate();
  
  picker.set('highlight', [year, month, day], { muted });
};

// ============================================================================
// MODIFICACIÓN DEL ESTADO DEL PICKER (Genéricas)
// ============================================================================

/**
 * Sets a picker property with muted flag
 * @param {jQuery} $input - jQuery input element
 * @param {string} property - Property to set ('min', 'max', 'select', etc.)
 * @param {*} value - Value to set
 */
export const setPickerProperty = ($input, property, value) => {
  $input.pickadate('picker').set(property, value, { muted: true });
};

/**
 * Set a date on the picker
 * @param {Object} config - Configuration object
 * @param {jQuery} config.$input - jQuery input element
 * @param {string} config.value - The date value to set
 * @param {string} config.openTicketText - Text for open ticket option
 * @param {string} config.dateFormat - Date format string
 * @param {Function} config.setOpenDateFn - Function to set open date
 * @param {Function} config.stringToDateFn - Function to convert string to date
 */
export const setPickerDate = ({ $input, value, openTicketText, dateFormat, setOpenDateFn, stringToDateFn }) => {
  if (!$input) return;

  if (value === openTicketText) {
    setOpenDateFn();
    return;
  }

  const newDate = stringToDateFn(value, dateFormat);
  if (!newDate) return;

  $input
    .pickadate('picker')
    .set('select', [newDate.getFullYear(), newDate.getMonth(), newDate.getDate()], {
      muted: true,
    });
};

// ============================================================================
// AGREGAR ELEMENTOS AL PICKER (Genéricas)
// ============================================================================

/**
 * Generic function to add any element to the calendar footer
 * Handles finding the footer, retry logic, and re-insertion if footer regenerates
 * 
 * @param {Object} config - Configuration object
 * @param {jQuery} config.$input - jQuery input element
 * @param {jQuery|string} config.element - jQuery element or HTML string to insert
 * @param {Object} config.insertedRef - Ref to track if element was inserted (optional)
 * @param {Function} config.onReinsert - Callback to recreate element if footer regenerates (optional)
 * @param {string} config.insertPosition - Position to insert: 'after-today' (default), 'prepend', 'append'
 * @param {string} config.selector - CSS selector to find existing element before insertion (optional, for cleanup)
 */
export const addElementToFooter = ({ 
  $input, 
  element, 
  insertedRef, 
  onReinsert,
  insertPosition = 'after-today',
  selector = null,
}) => {
  if (!$input) return;

  if (insertedRef && insertedRef.current) {
    return;
  }

  // Get picker instance to find footer
  // This works both in desktop (picker in parent) and mobile (picker moved to portal)
  // When picker is moved to portal, $root still points to the correct element
  const picker = $input.pickadate ? $input.pickadate('picker') : null;
  const $pickerRoot = picker && picker.$root ? picker.$root : $($input).parent().find('.picker');
  // Fallback: also check portal in case picker was moved there
  let $footer = $pickerRoot.find('.picker__footer');
  if (!$footer.length) {
    $footer = $('#reservamosPortal').find('.picker__footer');
  }

  if (!$footer.length) {
    // Footer might not be rendered yet, wait a bit and retry
    setTimeout(() => {
      addElementToFooter({ $input, element, insertedRef, onReinsert, insertPosition, selector });
    }, 100);
    return;
  }

  // Remove existing element if selector is provided (for cleanup)
  if (selector) {
    $footer.find(selector).remove();
  }

  // Convert string to jQuery element if needed
  const $elementToInsert = typeof element === 'string' ? $(element) : element;

  // Insert element based on position
  if (insertPosition === 'after-today') {
    const $todayButton = $footer.find('.picker__button--today');
    if ($todayButton.length) {
      $todayButton.after($elementToInsert);
    } else {
      // Fallback: prepend to footer if today button not found
      $footer.prepend($elementToInsert);
    }
  } else if (insertPosition === 'prepend') {
    $footer.prepend($elementToInsert);
  } else if (insertPosition === 'append') {
    $footer.append($elementToInsert);
  }

  // Set up re-insertion listener if onReinsert callback is provided
  if (onReinsert && picker) {
    const checkElementOnOpen = () => {
      setTimeout(() => {
        // Use picker's $root to find footer (works in both desktop and mobile)
        const $currentPickerRoot = picker.$root || $($input).parent().find('.picker');
        const $currentFooter = $currentPickerRoot.find('.picker__footer');
        const $existingElement = selector 
          ? $currentFooter.find(selector)
          : $currentFooter.find($elementToInsert);

        // If element is missing but we think it was inserted, re-insert it
        if ($existingElement.length === 0 && insertedRef && insertedRef.current) {
          // Reset ref to allow re-insertion
          insertedRef.current = false;
          // Re-insert using the callback
          onReinsert();
        }
      }, 50); // Small delay to ensure footer is rendered
    };

    // Check when calendar opens
    picker.on('open', checkElementOnOpen);
    
    // Also check immediately if calendar is already open
    if (picker.get('open')) {
      checkElementOnOpen();
    }
  }

  if (insertedRef) {
    insertedRef.current = true;
  }
};

/**
 * Add HTML content after a specific selector in the picker
 * Generic helper for adding content to different parts of the calendar
 * @param {Object} config - Configuration object
 * @param {jQuery} config.$input - jQuery input element
 * @param {string} config.selector - CSS selector to find the target element
 * @param {string} config.html - HTML content to add
 * @param {string} config.method - Method to use: 'after', 'before', 'append', 'prepend' (default: 'after')
 * @param {Object} config.insertedRef - Ref to track if element was inserted (optional)
 */
export const addPickerContent = ({ $input, selector, html, method = 'after', insertedRef }) => {
  if (!$input) return;
  
  if (insertedRef && insertedRef.current) {
    return;
  }

  // Use picker.$root to locate elements regardless of whether the picker
  // was moved to #reservamosPortal (mobile) or stays in the parent (desktop).
  // This matches the pattern used in addElementToFooter and addEventEmojisToCalendar.
  const picker = $input.pickadate ? $input.pickadate('picker') : null;
  const $pickerRoot = picker && picker.$root ? picker.$root : $($input).parent().find('.picker');
  let $target = $pickerRoot.find(selector);
  if (!$target.length) {
    // Fallback: picker may have been moved to portal on mobile
    $target = $('#reservamosPortal').find(selector);
  }

  if (!$target.length) {
    setTimeout(() => {
      addPickerContent({ $input, selector, html, method, insertedRef });
    }, 100);
    return;
  }

  switch (method) {
    case 'after':
      $target.after(html);
      break;
    case 'before':
      $target.before(html);
      break;
    case 'append':
      $target.append(html);
      break;
    case 'prepend':
      $target.prepend(html);
      break;
    default:
      $target.after(html);
  }

  if (insertedRef) {
    insertedRef.current = true;
  }
};

/**
 * Generic function to add a button to the picker
 * 
 * @param {Object} config - Configuration object
 * @param {jQuery} config.$input - jQuery input element
 * @param {Object} config.buttonConfig - Button configuration
 * @param {string} config.buttonConfig.text - Button text
 * @param {string} config.buttonConfig.className - CSS class name
 * @param {Function} config.buttonConfig.onClick - Click handler
 * @param {string} config.position - Position to insert: 'footer-after-today', 'footer-append', or selector for addPickerContent
 * @param {Object} config.insertedRef - Ref to track if button was inserted (optional)
 */
export const addButtonToPicker = ({ $input, buttonConfig, position = 'footer-after-today', insertedRef }) => {
  if (!$input) return;

  const $button = createPickerButton(buttonConfig);

  if (position.startsWith('footer-')) {
    const insertPosition = position === 'footer-after-today' ? 'after-today' : 'append';
    addElementToFooter({
      $input,
      element: $button,
      insertedRef,
      insertPosition,
    });
  } else {
    // Use addPickerContent for other positions
    addPickerContent({
      $input,
      selector: position,
      html: $button,
      method: 'after',
      insertedRef,
    });
  }
};

/**
 * Generic function to add content to the picker
 * Wrapper around addPickerContent for consistency
 * 
 * @param {Object} config - Configuration object
 * @param {jQuery} config.$input - jQuery input element
 * @param {string} config.selector - CSS selector to find the target element
 * @param {string} config.html - HTML content to add
 * @param {string} config.method - Method to use: 'after', 'before', 'append', 'prepend' (default: 'after')
 * @param {Object} config.insertedRef - Ref to track if element was inserted (optional)
 */
export const addContentToPicker = ({ $input, selector, html, method = 'after', insertedRef }) => {
  addPickerContent({ $input, selector, html, method, insertedRef });
};

/**
 * Adds event emoji to a specific calendar day cell
 * The emoji is added above the day number in the calendar cell
 * 
 * @param {Object} config - Configuration object
 * @param {jQuery} config.$dayCell - jQuery element for the calendar day cell
 * @param {string} config.normalizedDate - Normalized date in YYYY-MM-DD format
 * @param {Object} config.eventsByDate - Map of normalized dates to event types
 */
const addEmojiToDayCell = ({ $dayCell, normalizedDate, eventsByDate }) => {
  if (!normalizedDate || !eventsByDate[normalizedDate]) return;

  // Check if emoji already exists (avoid duplicates)
  if ($dayCell.find('.calendar-event-emoji').length > 0) return;

  // Get emoji for this event type
  const emoji = getEventEmoji(eventsByDate[normalizedDate]);
  if (!emoji) return;

  // Create emoji element using generic helper
  const $emoji = createEventEmoji({ emoji });

  // Insert emoji at the beginning of the day cell (above the number)
  // The day number is the text content of the cell, so prepending puts emoji first
  $dayCell.prepend($emoji);
};

/**
 * Adds event emojis to calendar days based on events data
 * The emoji is added above the day number in the calendar cell
 * 
 * @param {Object} config - Configuration object
 * @param {jQuery} config.$input - jQuery input element
 * @param {Array} config.events - Array of event objects with date and type properties
 */
export const addEventEmojisToCalendar = ({ $input, events = [] }) => {
  if (!$input || !events || events.length === 0) return;

  const picker = $input.pickadate('picker');
  if (!picker) return;

  // Find the calendar table using picker's $root property
  // This works both in desktop (picker in parent) and mobile (picker moved to portal)
  // When picker is moved to portal, $root still points to the correct element
  const $pickerRoot = picker.$root || $($input).parent().find('.picker');
  // Fallback: also check portal in case picker was moved there
  const $calendarTable = $pickerRoot.find('.picker__table').length 
    ? $pickerRoot.find('.picker__table')
    : $('#reservamosPortal').find('.picker__table');

  if (!$calendarTable.length) return;

  // Map events to date lookup object
  const eventsByDate = mapEventsByDate(events);

  // Process each day cell in the calendar
  $calendarTable.find('.picker__day').each(function() {
      const $dayCell = $(this);
      
      // Get the data-pick attribute which contains the timestamp
      const dataPick = $dayCell.attr('data-pick');
      if (!dataPick) return;

      // Convert timestamp to Date and normalize to YYYY-MM-DD
      const dayDate = new Date(parseInt(dataPick, 10));
      const normalizedDate = formatDateToYYYYMMDD(dayDate);
      
      // Add emoji to this day cell if it has an event
      addEmojiToDayCell({ $dayCell, normalizedDate, eventsByDate });
    });
};


/**
 * Adds event badges to the calendar footer
 * Badges are visual indicators showing which event types are present in the calendar
 * They are non-functional (display only)
 * 
 * @param {Object} config - Configuration object
 * @param {jQuery} config.$input - jQuery input element
 * @param {Array} config.events - Array of event objects with type property
 * @param {Object} config.insertedRef - Ref to track if badges were inserted (optional)
 * @param {Object} config.dictionary - Translation dictionary for event labels (from WidgetConfig context)
 */
export const addEventBadgesToFooter = ({ $input, events = [], insertedRef, dictionary = null }) => {
  if (!$input || !dictionary) return;

  // Map events to badges with translations
  const badges = mapEventsToBadges(events, dictionary);
  if (badges.length === 0) return;

  // Create badges container with all badges using generic helper
  // Pass getEventEmoji as a function to createEventBadge
  const $badgesContainer = createBadgesContainer(
    badges,
    ({ type, label }) => createEventBadge({ type, label, getEmoji: getEventEmoji })
  );

  // Use generic function to add element to footer
  addElementToFooter({
    $input,
    element: $badgesContainer,
    insertedRef,
    insertPosition: 'after-today',
    selector: '.calendar-event-badges-container',
      onReinsert: () => {
        // Re-insert badges if footer regenerates
        addEventBadgesToFooter({ $input, events, insertedRef, dictionary });
      },
  });
};

/**
 * Updates event badges in the calendar footer based on events in the visible month
 * This function orchestrates filtering events and adding/removing badges accordingly
 * 
 * @param {Object} config - Configuration object
 * @param {jQuery} config.$input - jQuery input element
 * @param {Array} config.events - Array of event objects with date and type properties
 * @param {Date} config.currentMonth - Current visible month (optional, falls back to getting from $input)
 * @param {Object} config.insertedRef - Ref to track if badges were inserted (optional)
 * @param {Object} config.dictionary - Translation dictionary for event labels (from WidgetConfig context)
 */
export const updateEventBadgesForVisibleMonth = ({ $input, events = [], currentMonth = null, insertedRef, dictionary = null }) => {
  if (!$input) return;

  // Helper function to get picker root element
  // Works in both desktop (picker in parent) and mobile (picker moved to portal)
  const getPickerRoot = () => {
    const picker = $input.pickadate ? $input.pickadate('picker') : null;
    return picker && picker.$root ? picker.$root : $input.parent().find('.picker');
  };

  // Remove badges if no events
  if (!events || events.length === 0) {
    const $pickerRoot = getPickerRoot();
    if ($pickerRoot) {
      const $footer = $pickerRoot.find('.picker__footer');
      $footer.find('.calendar-event-badges-container').remove();
    }
    if (insertedRef) {
      insertedRef.current = false;
    }
    return;
  }

  // Filter events to only include those in the visible month
  // Use currentMonth if available, otherwise fallback to getting it from $input
  const eventsInVisibleMonth = filterEventsByVisibleMonth(events, $input, currentMonth);

  // Only add badges if there are events in the visible month
  if (eventsInVisibleMonth.length > 0) {
    // Reset ref to allow re-insertion
    if (insertedRef) {
      insertedRef.current = false;
    }
    addEventBadgesToFooter({
      $input,
      events: eventsInVisibleMonth,
      insertedRef,
      dictionary, // Pass translation dictionary for event labels
    });
  } else {
    // Remove badges if no events in visible month
    const $pickerRoot = getPickerRoot();
    if ($pickerRoot) {
      const $footer = $pickerRoot.find('.picker__footer');
      $footer.find('.calendar-event-badges-container').remove();
    }
    if (insertedRef) {
      insertedRef.current = false;
    }
  }
};


/**
 * Add the date picker text to the calendar
 * @param {Object} config - Configuration object
 * @param {jQuery} config.$input - jQuery input element
 * @param {string} config.field - Field name (departureDate or returnDate)
 * @param {string} config.departureDatePickerText - Text for departure date picker
 * @param {string} config.returnDatePickerText - Text for return date picker
 * @param {string} config.title - Title for the calendar
 */
export const setDatePickerText = ({ $input, field, departureDatePickerText, returnDatePickerText, title }) => {
  /* global $ */
  if (!$input) return;

  const datePickerText = field === 'departureDate' 
    ? departureDatePickerText 
    : returnDatePickerText;

  // Use picker.$root to locate .picker__box regardless of whether the picker
  // was moved to #reservamosPortal (mobile) or stays in the parent (desktop).
  const picker = $input.pickadate ? $input.pickadate('picker') : null;
  const $pickerRoot = picker && picker.$root ? picker.$root : $($input).parent().find('.picker');
  let $pickerBox = $pickerRoot.find('.picker__box');
  if (!$pickerBox.length) {
    // Fallback: picker may have been moved to portal on mobile
    $pickerBox = $('#reservamosPortal').find('.picker__box');
  }

  // Check if datePickerTextContainer already exists to prevent duplicates
  const existingContainer =
    $pickerRoot.find('.datePickerTextContainer').length > 0 ||
    $('#reservamosPortal').find('.datePickerTextContainer').length > 0;

  if (existingContainer) {
    return;
  }

  $pickerBox.before(`
      <div class="datePickerTextContainer">
        <div class="calendar-heading">
          <p class="calendar-heading-title">${title}</p>
          ${
            datePickerText
              ? `<div class="calendar-heading-message">
                <label>${datePickerText}</label>
              </div>`
              : ''
          }
        </div>
      </div>
    `);
};
